import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';

import {
  modelLabTrainingJobApi,
} from '../../services/api';
import type { ExperimentComparison } from '../../types';
import { ErrorAlert, LoadingSpinner, PageHeader } from '../common';
import { getApiErrorMessage, trainingStatusColor } from './utils';
import { ConfusionMatrix, ROCCurve, FeatureImportance } from './charts';

const ModelLabExperimentsPage: React.FC = () => {
  const [selectedJobIds, setSelectedJobIds] = useState<number[]>([]);
  const [comparison, setComparison] = useState<ExperimentComparison[] | null>(null);

  const jobsQuery = useQuery({
    queryKey: ['model-lab-training-jobs-for-experiments'],
    queryFn: () => modelLabTrainingJobApi.listSummary({ page_size: 200 }),
  });

  const compareMutation = useMutation({
    mutationFn: (ids: number[]) => modelLabTrainingJobApi.compare({ job_ids: ids }),
    onSuccess: (data) => {
      setComparison(data.comparisons);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Comparison failed.'));
    },
  });

  const completedJobs = (jobsQuery.data?.results || []).filter(
    (j) => j.status === 'COMPLETED'
  );

  if (jobsQuery.error) return <ErrorAlert error={jobsQuery.error as Error} />;
  if (jobsQuery.isLoading) return <LoadingSpinner message="Loading jobs..." />;

  // Extract all unique scalar metric keys from comparison results
  const metricKeys = comparison
    ? Array.from(
        new Set(
          comparison.flatMap((c) =>
            Object.entries(c.metrics || {})
              .filter(([, v]) => typeof v === 'number')
              .map(([k]) => k)
          )
        )
      )
    : [];

  return (
    <Box>
      <PageHeader
        title="Experiment Comparison"
        breadcrumbs={[
          { label: 'Model Lab', to: '/ai/model-lab' },
          { label: 'Experiments' },
        ]}
      />

      <Stack spacing={3}>
        <Alert severity="info">
          Compare completed training jobs side-by-side to identify the best-performing
          model configuration. Select at least two jobs, then click Compare.
        </Alert>

        {/* Selector */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Select completed training jobs to compare
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
              <Autocomplete
                multiple
                options={completedJobs}
                getOptionLabel={(opt) =>
                  `#${opt.id} ${opt.model_asset_name} — ${opt.dataset_version_name}`
                }
                value={completedJobs.filter((j) => selectedJobIds.includes(j.id))}
                onChange={(_, newVal) => setSelectedJobIds(newVal.map((j) => j.id))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Training jobs"
                    placeholder={completedJobs.length > 0 ? 'Search completed jobs...' : 'No completed jobs available'}
                    helperText={
                      selectedJobIds.length < 2
                        ? `Select at least 2 jobs to compare (${selectedJobIds.length} selected)`
                        : `${selectedJobIds.length} jobs selected`
                    }
                  />
                )}
                sx={{ minWidth: 400, flexGrow: 1 }}
                noOptionsText="No completed training jobs found. Train a model first."
              />
              <Button
                variant="contained"
                startIcon={<CompareArrowsIcon />}
                onClick={() => compareMutation.mutate(selectedJobIds)}
                disabled={selectedJobIds.length < 2 || compareMutation.isPending}
              >
                Compare
              </Button>
              {comparison && comparison.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const headers = ['ID', 'Model', 'Algorithm', ...metricKeys];
                    const rows = comparison.map((exp) => [
                      exp.id,
                      exp.model_name,
                      exp.algorithm || '',
                      ...metricKeys.map((k) => {
                        const val = (exp.metrics as any)?.[k];
                        return typeof val === 'number' ? val : '';
                      }),
                    ]);
                    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `experiment_comparison_${Date.now()}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('CSV exported.');
                  }}
                >
                  Export CSV
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Comparison results */}
        {comparison && comparison.length > 0 && (
          <>
            {/* Metrics table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job</TableCell>
                    <TableCell>Algorithm</TableCell>

                    {metricKeys.map((k) => (
                      <TableCell key={k} align="right">{k}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparison.map((exp) => {
                    // Find the best value per metric to highlight
                    return (
                      <TableRow key={exp.id} hover>
                        <TableCell>
                          #{exp.id}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {exp.model_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={exp.algorithm || '—'} />
                        </TableCell>
                        {metricKeys.map((k) => {
                          const val = (exp.metrics as any)?.[k];
                          const isNumeric = typeof val === 'number';
                          // Highlight best value
                          const allVals = comparison
                            .map((c) => (c.metrics as any)?.[k])
                            .filter((v) => typeof v === 'number');
                          const best = Math.max(...allVals);
                          const isBest = isNumeric && val === best && allVals.length > 1;
                          return (
                            <TableCell
                              key={k}
                              align="right"
                              sx={isBest ? { fontWeight: 'bold', color: 'success.main' } : {}}
                            >
                              {isNumeric ? val.toFixed(4) : '—'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Side-by-side charts */}
            <Typography variant="subtitle1">Visual Comparison</Typography>
            <Grid container spacing={3}>
              {comparison.map((exp) => (
                <Grid size={{ xs: 12, md: 6 }} key={exp.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Job #{exp.id} — {exp.algorithm || 'Unknown'}
                      </Typography>

                      <Stack spacing={2}>
                        {(exp.metrics as any)?.confusion_matrix && (
                          <Box>
                            <Typography variant="caption">Confusion Matrix</Typography>
                            <ConfusionMatrix data={(exp.metrics as any).confusion_matrix} />
                          </Box>
                        )}
                        {(exp.metrics as any)?.roc_curve && (
                          <Box>
                            <Typography variant="caption">ROC Curve</Typography>
                            <ROCCurve data={(exp.metrics as any).roc_curve} />
                          </Box>
                        )}
                        {(exp.metrics as any)?.feature_importance &&
                          Array.isArray((exp.metrics as any).feature_importance) && (
                          <Box>
                            <Typography variant="caption">Feature Importance</Typography>
                            <FeatureImportance
                              data={(exp.metrics as any).feature_importance}
                              maxItems={10}
                            />
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {comparison && comparison.length === 0 && (
          <Alert severity="warning">
            No matching experiments found. Make sure selected jobs have completed with metrics.
          </Alert>
        )}

        {!comparison && (
          <Card variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
            <CardContent>
              <CompareArrowsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Comparison Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select two or more completed training jobs above and click Compare to see
                a side-by-side metrics table and visual charts.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default ModelLabExperimentsPage;
